/* eslint-disable max-lines, max-lines-per-function */
import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { ImageUpload, type UploadedImage } from '@/components/ui/image-upload'
import { Label } from '@/components/ui/label'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { Textarea } from '@/components/ui/textarea'
import {
  CapacityBelowSoldDialog,
  EventCapacityDetail,
  isLoweringCapacityBelowSold,
} from '@/components/events/event-capacity-below-sold'
import { apiMessage } from '@/lib/contacts/errors'
import { eventApiMessage } from '@/components/events/event-errors'
import {
  DetailField,
  EventContactControl,
  IdentityAndScheduleFields,
} from '@/components/events/event-details-fields'
import { useEventCapacity } from '@/components/events/use-event-capacity'
import { useEventHeroImageUpdate } from '@/components/events/use-event-hero-image-update'
import { EventChapterField } from '@/components/events/event-chapter-picker'
import { PreviousEditionField, previousEditionError } from './previous-edition-field'
import type { PreviousEdition, PreviousEditionLink } from './previous-edition-types'
import {
  isNotificationRequired,
  useEventDetailsUpdate,
} from '@/components/events/use-event-details-update'

export interface EventDetails extends PreviousEditionLink {
  id: number
  name: string
  status: 'draft' | 'published' | 'cancelled'
  starts_at_local: string
  ends_at_local: string | null
  location: string | null
  description: string | null
  info_for_attendees?: string | null
  event_contact_name?: string | null
  event_contact_email?: string | null
  capacity?: number | null
  sold_count?: number
  hero_image: (UploadedImage & { id: number; content_type: string }) | null
  chapter?: { id: number; name: string } | null
}

export function EventDetailsForm({ event }: { event: EventDetails }) {
  const queryClient = useQueryClient()
  const [name, setName] = React.useState(event.name)
  const [startsAt, setStartsAt] = React.useState(event.starts_at_local.slice(0, 16))
  const [endsAt, setEndsAt] = React.useState(event.ends_at_local?.slice(0, 16) ?? '')
  const [location, setLocation] = React.useState(event.location ?? '')
  const [description, setDescription] = React.useState(event.description ?? '')
  const [infoForAttendees, setInfoForAttendees] = React.useState(event.info_for_attendees ?? '')
  const [eventContactName, setEventContactName] = React.useState(event.event_contact_name ?? '')
  const [eventContactEmail, setEventContactEmail] = React.useState(event.event_contact_email ?? '')
  const [editingEventContact, setEditingEventContact] = React.useState(false)
  const [heroImage, setHeroImage] = React.useState(event.hero_image)
  const [chapterId, setChapterId] = React.useState<number | null>(event.chapter?.id ?? null)
  const [previousEdition, setPreviousEdition] = React.useState(() => event.previous_edition ?? null)
  const chaptersQuery = useQuery({
    queryKey: ['event-chapters'],
    queryFn: () =>
      import('@/lib/api').then(({ getJSON }) =>
        getJSON<{ chapters: { id: number; name: string }[] }>('/api/v1/chapters'),
      ),
    enabled: event.chapter !== undefined,
    retry: false,
  })
  const [notificationRequired, setNotificationRequired] = React.useState(false)
  const [notifyRegistrants, setNotifyRegistrants] = React.useState(true)
  const [capacityConfirmationOpen, setCapacityConfirmationOpen] = React.useState(false)
  const eventCapacity = useEventCapacity(event.capacity)
  const update = useEventDetailsUpdate({
    event,
    values: {
      name,
      startsAt,
      endsAt,
      location,
      description,
      infoForAttendees,
      eventContactName,
      eventContactEmail,
      capacity: eventCapacity.value,
      chapterId,
      previousEditionId: previousEdition?.id ?? null,
    },
    onSuccess: () => setNotificationRequired(false),
    onNotificationRequired: () => {
      setNotificationRequired(true)
      setNotifyRegistrants(true)
    },
  })
  const updateHero = useEventHeroImageUpdate(event, queryClient, setHeroImage)
  const capacityBelowSold = isLoweringCapacityBelowSold({
    capacity: eventCapacity.capacity,
    currentCapacity: event.capacity,
    enabled: eventCapacity.enabled,
    soldCount: event.sold_count,
  })

  const saveDetails = (notificationChoice?: boolean) => {
    if (capacityBelowSold) {
      setCapacityConfirmationOpen(true)
      return
    }
    update.mutate(notificationChoice)
  }

  return (
    <EventDetailsFormContent
      event={event}
      previousEdition={previousEdition}
      onPreviousEditionChange={(value) => {
        setPreviousEdition(value)
        update.reset()
      }}
      eventCapacity={eventCapacity}
      name={name}
      startsAt={startsAt}
      endsAt={endsAt}
      location={location}
      description={description}
      infoForAttendees={infoForAttendees}
      eventContactName={eventContactName}
      eventContactEmail={eventContactEmail}
      editingEventContact={editingEventContact}
      heroImage={heroImage}
      chapterId={chapterId}
      chapters={chaptersQuery.data?.chapters ?? []}
      notificationRequired={notificationRequired}
      notifyRegistrants={notifyRegistrants}
      capacityConfirmationOpen={capacityConfirmationOpen}
      pending={update.isPending}
      saved={update.isSuccess}
      error={update.error}
      heroImagePending={updateHero.isPending}
      heroImageError={updateHero.error}
      onNameChange={setName}
      onStartChange={setStartsAt}
      onEndChange={setEndsAt}
      onLocationChange={setLocation}
      onDescriptionChange={setDescription}
      onInfoForAttendeesChange={setInfoForAttendees}
      onEditEventContact={() => setEditingEventContact(true)}
      onEventContactNameChange={setEventContactName}
      onEventContactEmailChange={setEventContactEmail}
      onClearEventContact={() => {
        setEventContactName('')
        setEventContactEmail('')
      }}
      onChooseHeroImage={(file) => updateHero.mutate(file)}
      onRemoveHeroImage={() => updateHero.mutate(null)}
      onChapterChange={setChapterId}
      onNotifyRegistrantsChange={setNotifyRegistrants}
      onSave={saveDetails}
      onCancelCapacityConfirmation={() => setCapacityConfirmationOpen(false)}
      onConfirmCapacity={() => {
        setCapacityConfirmationOpen(false)
        update.mutate(notificationRequired ? notifyRegistrants : undefined)
      }}
    />
  )
}

function EventDetailsFormContent({
  event,
  previousEdition,
  onPreviousEditionChange,
  eventCapacity,
  name,
  startsAt,
  endsAt,
  location,
  description,
  infoForAttendees,
  eventContactName,
  eventContactEmail,
  editingEventContact,
  heroImage,
  chapterId,
  chapters,
  notificationRequired,
  notifyRegistrants,
  capacityConfirmationOpen,
  pending,
  saved,
  error,
  heroImagePending,
  heroImageError,
  onNameChange,
  onStartChange,
  onEndChange,
  onLocationChange,
  onDescriptionChange,
  onInfoForAttendeesChange,
  onEditEventContact,
  onEventContactNameChange,
  onEventContactEmailChange,
  onClearEventContact,
  onChooseHeroImage,
  onRemoveHeroImage,
  onChapterChange,
  onNotifyRegistrantsChange,
  onSave,
  onCancelCapacityConfirmation,
  onConfirmCapacity,
}: EventDetailsFormContentProps) {
  return (
    <section className="space-y-2" aria-labelledby="event-details-heading">
      <h2 id="event-details-heading" className="text-caption font-medium text-muted-foreground">
        Details
      </h2>
      <form
        className="divide-y divide-border rounded-md border border-border bg-card"
        onSubmit={(formEvent) => {
          formEvent.preventDefault()
          onSave()
        }}
      >
        <IdentityAndScheduleFields
          event={event}
          name={name}
          startsAt={startsAt}
          endsAt={endsAt}
          onNameChange={onNameChange}
          onStartChange={onStartChange}
          onEndChange={onEndChange}
        />
        {chapters.length > 0 && (
          <EventChapterField chapters={chapters} value={chapterId} onChange={onChapterChange} />
        )}
        <PreviousEditionField
          eventId={event.id}
          startsAt={startsAt}
          value={previousEdition}
          onChange={onPreviousEditionChange}
          error={previousEditionError(error)}
          pending={pending}
        />
        <DetailField label="Location" htmlFor="event-location">
          <Input
            id="event-location"
            className="h-8"
            value={location}
            onChange={(inputEvent) => onLocationChange(inputEvent.target.value)}
          />
        </DetailField>
        <DetailField label="Image">
          <ImageUpload
            image={heroImage}
            label="Event hero image"
            pending={heroImagePending}
            onChoose={onChooseHeroImage}
            onRemove={onRemoveHeroImage}
          />
          {heroImageError instanceof Error && <Alert>{apiMessage(heroImageError)}</Alert>}
        </DetailField>
        <DetailField label="Description" htmlFor="event-description">
          <RichTextEditor
            id="event-description"
            aria-label="Description"
            value={description}
            onChange={onDescriptionChange}
          />
        </DetailField>
        <DetailField label="Event contact">
          <EventContactControl
            name={eventContactName}
            email={eventContactEmail}
            editing={editingEventContact}
            onEdit={onEditEventContact}
            onNameChange={onEventContactNameChange}
            onEmailChange={onEventContactEmailChange}
            onClear={onClearEventContact}
          />
        </DetailField>
        <EventInfoForAttendeesField value={infoForAttendees} onChange={onInfoForAttendeesChange} />
        <EventCapacityDetail eventCapacity={eventCapacity} soldCount={event.sold_count} />
        <EventDetailsSaveActions
          notificationRequired={notificationRequired}
          notifyRegistrants={notifyRegistrants}
          pending={pending}
          saved={saved}
          error={error}
          onNotifyRegistrantsChange={onNotifyRegistrantsChange}
          onSave={onSave}
        />
      </form>
      <CapacityConfirmation
        open={capacityConfirmationOpen}
        soldCount={event.sold_count ?? 0}
        capacity={eventCapacity.capacity}
        pending={pending}
        onCancel={onCancelCapacityConfirmation}
        onConfirm={onConfirmCapacity}
      />
    </section>
  )
}

function CapacityConfirmation({
  open,
  ...dialogProps
}: { open: boolean } & React.ComponentProps<typeof CapacityBelowSoldDialog>) {
  return open ? <CapacityBelowSoldDialog {...dialogProps} /> : null
}

function EventDetailsSaveActions({
  notificationRequired,
  notifyRegistrants,
  pending,
  saved,
  error,
  onNotifyRegistrantsChange,
  onSave,
}: Pick<
  EventDetailsFormContentProps,
  | 'notificationRequired'
  | 'notifyRegistrants'
  | 'pending'
  | 'saved'
  | 'error'
  | 'onNotifyRegistrantsChange'
  | 'onSave'
>) {
  return (
    <div className="px-4 py-3 sm:pl-[164px]">
      {notificationRequired ? (
        <NotificationChoice
          checked={notifyRegistrants}
          pending={pending}
          onCheckedChange={onNotifyRegistrantsChange}
          onSave={() => onSave(notifyRegistrants)}
        />
      ) : (
        <Button type="submit" variant="outline" className="h-8" disabled={pending}>
          {pending ? 'Saving…' : 'Save details'}
        </Button>
      )}
      {saved && <p className="text-caption text-muted-foreground">Details saved</p>}
      {error instanceof Error && !isNotificationRequired(error) && !previousEditionError(error) && (
        <Alert>{eventApiMessage(error)}</Alert>
      )}
    </div>
  )
}

interface EventDetailsFormContentProps {
  event: EventDetails
  previousEdition: PreviousEdition | null
  onPreviousEditionChange: (value: PreviousEdition | null) => void
  eventCapacity: ReturnType<typeof useEventCapacity>
  name: string
  startsAt: string
  endsAt: string
  location: string
  description: string
  infoForAttendees: string
  eventContactName: string
  eventContactEmail: string
  editingEventContact: boolean
  heroImage: EventDetails['hero_image']
  chapterId: number | null
  chapters: { id: number; name: string }[]
  notificationRequired: boolean
  notifyRegistrants: boolean
  capacityConfirmationOpen: boolean
  pending: boolean
  saved: boolean
  error: unknown
  heroImagePending: boolean
  heroImageError: unknown
  onNameChange: (value: string) => void
  onStartChange: (value: string) => void
  onEndChange: (value: string) => void
  onLocationChange: (value: string) => void
  onDescriptionChange: (value: string) => void
  onInfoForAttendeesChange: (value: string) => void
  onEditEventContact: () => void
  onEventContactNameChange: (value: string) => void
  onEventContactEmailChange: (value: string) => void
  onClearEventContact: () => void
  onChooseHeroImage: (file: File) => void
  onRemoveHeroImage: () => void
  onChapterChange: (value: number | null) => void
  onNotifyRegistrantsChange: (checked: boolean) => void
  onSave: (notificationChoice?: boolean) => void
  onCancelCapacityConfirmation: () => void
  onConfirmCapacity: () => void
}

function NotificationChoice({
  checked,
  pending,
  onCheckedChange,
  onSave,
}: {
  checked: boolean
  pending: boolean
  onCheckedChange: (checked: boolean) => void
  onSave: () => void
}) {
  return (
    <div className="space-y-2">
      <p className="text-body-sm font-medium">
        The Event has Orders. Notify registrants about this change?
      </p>
      <div className="flex items-center gap-2">
        <Checkbox
          id="notify-event-registrants"
          checked={checked}
          onCheckedChange={(value) => onCheckedChange(value === true)}
        />
        <Label htmlFor="notify-event-registrants" className="text-caption">
          Notify registrants by email
        </Label>
      </div>
      <Button type="button" variant="outline" className="h-8" disabled={pending} onClick={onSave}>
        {pending ? 'Saving…' : checked ? 'Save and notify' : 'Save without notifying'}
      </Button>
    </div>
  )
}

function EventInfoForAttendeesField({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  return (
    <DetailField label="Info for attendees" htmlFor="event-info-for-attendees">
      <Textarea
        id="event-info-for-attendees"
        rows={2}
        className="min-h-0"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="mt-1.5 text-caption text-muted-foreground">
        Included in confirmation and reminder emails. Not shown on the event page.
      </p>
    </DetailField>
  )
}
